import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { DataProvider, useData } from './context/DataContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import ErrorBoundary from './components/common/ErrorBoundary';
import Login from './components/Auth/Login';
import MainLayout from './components/Layout/MainLayout';
import Dashboard from './components/Dashboard/Dashboard';
import Accounts from './components/Accounts/Accounts';
import AccountForm from './components/Accounts/AccountForm';
import Portfolio from './components/Portfolio/Portfolio';
import Goals from './components/Goals/Goals';
import GoalDetail from './components/Goals/GoalDetail';
import Allocations from './components/Allocations/Allocations';
import Transactions from './components/Transactions/Transactions';
import Reports from './components/Reports/Reports';
import ImportExport from './components/ImportExport/ImportExport';
import ImportTransactions from './components/ImportExport/ImportTransactions';
import Settings from './components/Settings/Settings';
import FundPrices from './components/FundPrices/FundPrices';
import Properties from './components/Properties/Properties';
import PropertyDetail from './components/Properties/PropertyDetail';
import Mortgages from './components/Mortgages/Mortgages';
import MortgageDetail from './components/Mortgages/MortgageDetail';
import RealEstate from './components/RealEstate/RealEstate';
import SchoolFeesPage from './components/SchoolFees/SchoolFeesPage';
import SchoolFeePlanDetail from './components/SchoolFees/SchoolFeePlanDetail';

// Loading Spinner Component
const LoadingSpinner = () => (
  <div className="flex flex-col items-center justify-center min-h-screen gap-4">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
    <p className="text-gray-600">Loading...</p>
  </div>
);

const AppContent = () => {
  const { loading } = useData();
  const skipAuth = import.meta.env.VITE_SKIP_AUTH === 'true';
  const { currentUser } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!skipAuth && !currentUser) {
    return <Login />;
  }

  return (
    <Router>
      <MainLayout>
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/accounts" element={<Accounts />} />
          <Route path="/accounts/new" element={<AccountForm />} />
          <Route path="/accounts/edit/:id" element={<AccountForm />} />
          <Route path="/portfolio/:id" element={<Portfolio />} />
          <Route path="/funds" element={<Goals />} />
          <Route path="/funds/:id" element={<GoalDetail />} />
          <Route path="/allocations" element={<Allocations />} />
          <Route path="/transactions" element={<Transactions />} />
          <Route path="/reports" element={<Reports />} />
          <Route path="/real-estate" element={<RealEstate />} />
          <Route path="/properties" element={<Properties />} />
          <Route path="/properties/:id" element={<PropertyDetail />} />
          <Route path="/mortgages" element={<Mortgages />} />
          <Route path="/mortgages/:id" element={<MortgageDetail />} />
          <Route path="/fund-prices" element={<FundPrices />} />
          <Route path="/import-export" element={<ImportExport />} />
          <Route path="/import-transactions" element={<ImportTransactions />} />
          <Route path="/school-fees" element={<SchoolFeesPage />} />
          <Route path="/school-fees/:id" element={<SchoolFeePlanDetail />} />
          <Route path="/settings" element={<Settings />} />
        </Routes>
      </MainLayout>
    </Router>
  );
};

export default function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <DataProvider>
          <AppContent />
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: '#fff',
                color: '#0A0B0D',
                border: '1px solid #E1E6ED',
              borderRadius: '12px',
              boxShadow: '0 10px 15px -3px rgba(10, 11, 13, 0.08)',
            },
            success: {
              iconTheme: {
                primary: '#10B981',
                secondary: '#fff',
              },
            },
            error: {
              iconTheme: {
                primary: '#EF4444',
                secondary: '#fff',
              },
            },
          }}
        />
      </DataProvider>
    </AuthProvider>
    </ErrorBoundary>
  );
}
